import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";

function StarRating({ value }) {
    const v = Number(value) || 0;
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    className={`w-4 h-4 ${
                        v >= n
                            ? "fill-magni-navy text-magni-navy"
                            : v >= n - 0.5
                              ? "fill-magni-navy/50 text-magni-navy"
                              : "text-magni-light"
                    }`}
                />
            ))}
        </div>
    );
}

export default function AdminFeedback() {
    const [stats, setStats] = useState(null);
    const [list, setList] = useState([]);
    const [filterStore, setFilterStore] = useState("");
    const [stores, setStores] = useState([]);

    const load = async () => {
        const [s, l, ss] = await Promise.all([
            api.get("/admin/feedback/stats"),
            api.get("/admin/feedback", {
                params: filterStore ? { store_id: filterStore } : {},
            }),
            api.get("/admin/stores"),
        ]);
        setStats(s.data);
        setList(l.data);
        setStores(ss.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStore]);

    return (
        <div>
            <div className="mb-8">
                <div className="overline mb-2">Backoffice</div>
                <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                    Feedback clienti
                </h1>
                <p className="text-sm text-magni-muted mt-1">
                    Valutazioni e NPS raccolti dopo la prova MagniStretch®.
                </p>
            </div>

            {/* Global stats */}
            {stats?.global ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white border border-magni-light p-6">
                        <div className="overline mb-2">Risposte totali</div>
                        <div className="font-serif-display text-4xl text-magni-navy">
                            {stats.global.count}
                        </div>
                    </div>
                    <div className="bg-white border border-magni-light p-6">
                        <div className="overline mb-2">Stelle medie</div>
                        <div className="font-serif-display text-4xl text-magni-navy flex items-center gap-3">
                            {stats.global.avg_rating}
                            <StarRating value={stats.global.avg_rating} />
                        </div>
                    </div>
                    <div className="bg-white border border-magni-light p-6">
                        <div className="overline mb-2">NPS medio (1-10)</div>
                        <div className="font-serif-display text-4xl text-magni-navy">
                            {stats.global.avg_nps}
                            <span className="text-base text-magni-muted ml-1">/10</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white border border-magni-light p-8 mb-8 text-center text-magni-muted">
                    Nessun feedback ricevuto ancora. Le valutazioni vengono raccolte dopo il
                    check-in del cliente in negozio.
                </div>
            )}

            {/* Per store */}
            {stats?.per_store?.length > 0 && (
                <div className="bg-white border border-magni-light mb-8">
                    <div className="px-6 py-4 border-b border-magni-light">
                        <h2 className="font-serif-display text-2xl text-magni-navy">
                            Per negozio
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-magni-cream text-magni-muted">
                                <tr>
                                    <th className="text-left px-6 py-3 font-medium">Negozio</th>
                                    <th className="text-left px-6 py-3 font-medium">Risposte</th>
                                    <th className="text-left px-6 py-3 font-medium">Stelle</th>
                                    <th className="text-left px-6 py-3 font-medium">NPS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.per_store.map((s) => (
                                    <tr
                                        key={s.store_id}
                                        className="border-t border-magni-light"
                                    >
                                        <td className="px-6 py-3 text-magni-navy font-medium">
                                            {s.store_name}
                                        </td>
                                        <td className="px-6 py-3 text-magni-muted">
                                            {s.count}
                                        </td>
                                        <td className="px-6 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-magni-navy font-semibold">
                                                    {s.avg_rating}
                                                </span>
                                                <StarRating value={s.avg_rating} />
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-magni-navy">
                                            {s.avg_nps}
                                            <span className="text-xs text-magni-muted">/10</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Filter + comments list */}
            <div className="flex items-center gap-3 mb-4">
                <select
                    value={filterStore}
                    onChange={(e) => setFilterStore(e.target.value)}
                    className="magni-input max-w-xs"
                >
                    <option value="">Tutti i negozi</option>
                    {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
                <div className="text-xs text-magni-muted">{list.length} commenti</div>
            </div>

            <div className="space-y-3">
                {list.length === 0 ? (
                    <div className="bg-white border border-magni-light p-8 text-center text-magni-muted">
                        Nessun feedback per il filtro selezionato.
                    </div>
                ) : (
                    list.map((f) => (
                        <div key={f.id} className="bg-white border border-magni-light p-5">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div>
                                    <div className="font-semibold text-magni-navy">
                                        {f.customer_name || f.customer_email}
                                    </div>
                                    <div className="text-xs text-magni-muted">
                                        {f.store_name} · {f.created_at?.slice(0, 16).replace("T", " ")}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <StarRating value={f.rating} />
                                    <span className="text-xs uppercase tracking-wider text-magni-muted">
                                        NPS
                                    </span>
                                    <span className="font-semibold text-magni-navy">
                                        {f.nps}/10
                                    </span>
                                </div>
                            </div>
                            {f.comment && (
                                <p className="text-sm text-magni-navy whitespace-pre-line mt-3 border-t border-magni-light pt-3">
                                    {f.comment}
                                </p>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
